import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { IStudent } from '../../../models/students.model';
import { doGetStudentCoupons, doDeleteCoupon } from '../../../store/studentCoupon.slice';
import { doRemoveAuth } from '../../../store/auth.slice';
import { RootState } from '../../../store/rootReducer';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Button as AntButton, Modal, Table, TableColumnsType } from 'antd';

import Button from '../../Button';
import Pagination from '../../Pagination';
import StudentName from './Name';
import { warningVariant, errorVariant, successVariant } from '../../../lib/barVariant';
import config from '../../../lib/config';
import Loader from '../../Loader';
import IssueCouponModal from './modals/IssueCouponModal';
import useAuth from '../../../hooks/useAuth';

interface IProps {
  student: IStudent|undefined;
}

const StudentCoupon: FC<IProps> = ({ student }) => {
  const { writeAuthYn } = useAuth();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const dispatch = useDispatch();

  const { studentCoupons, isLoading } = useSelector((state: RootState) => state.studentCoupon);

  const [page, setPage] = useState(1);
  const [issueCouponVisible, setIssueCouponVisible] = useState(false);
  const [couponCode, setCouponCode] = useState<number | null>(null);
  const [deleteCouponVisible, setDeleteCouponVisible] = useState(false);

  useEffect(() => {
    if (student) getStudentCoupons(student.studentCode);
  }, []);

  const getStudentCoupons = async (memberCode: string) => {
    const res = await dispatch(doGetStudentCoupons(memberCode));

    switch (res?.status) {
      case 200:
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const onDeleteCoupon = async () => {
    if (!student || !couponCode) return;

    const res = await dispatch(doDeleteCoupon(couponCode));

    switch (res?.status) {
      case 200:
        getStudentCoupons(student.studentCode);
        enqueueSnackbar(t('common.message.delete'), successVariant);
        setCouponCode(null);
        setDeleteCouponVisible(false);
        break;
      case 401:
        dispatch(doRemoveAuth());
        break;
      case 403:
        enqueueSnackbar(t('common.message.forbidden'), warningVariant);
        history.push('/');
        break;
      default:
        enqueueSnackbar(res?.data?.data || t('common.message.error'), errorVariant);
    }
  }

  const columns: TableColumnsType<any> = [
    {
      title: t('student.student.column99'),
      dataIndex: 'couponName',
      key: 'couponName',
      sorter: (a, b) => a.couponName?.localeCompare(b.couponName)
    },
    {
      title: t('student.student.column100'),
      dataIndex: 'amount',
      key: 'amount',
      sorter: (a, b) => a.amount - b.amount,
      render: (value: number) => value.toLocaleString()
    },
    {
      title: t('student.student.column109'),
      dataIndex: 'expireDate',
      key: 'expireDate',
      sorter: (a, b) => a.expireDate?.localeCompare(b.expireDate),
      render: (value: string) => moment(value).format('YYYY.MM.DD')
    },
    {
      title: t('student.student.column110'),
      dataIndex: 'useYn',
      key: 'useYn',
      sorter: (a, b) => a.useYn?.localeCompare(b.useYn),
      render: (value, record) => (value === 'Y') ? (moment(record.lastUpdDttm).format('YYYY.MM.DD HH:mm')) || 'Y' : value
    },
    {
      title: t('common.button.delete'),
      dataIndex: 'couponStudentCode',
      key: 'couponStudentCode',
      sorter: (a, b) => a.couponStudentCode - b.couponStudentCode,
      render: (value: any, record: any) => (
        <div className="comm-btn__area justify-content-center">
          <Button
            className="btn-basic small gray"
            onClick={() => {
              setCouponCode(value);
              setDeleteCouponVisible(true);
            }}
            text={t('common.button.delete')}
            disabled={record.useYn === 'Y'}
            writeAuthYn={writeAuthYn}
          />
        </div>
      )
    },
    {
      title: t('common.text.note'),
      dataIndex: 'memo',
      key: 'memo',
      sorter: (a, b) => a.memo?.localeCompare(b.memo)
    },
  ];

  return (
    <>
      {isLoading ? <Loader /> : null}
      <StudentName student={student} />
      <div className="comm-bx__space">
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink-line"
            onClick={() => setIssueCouponVisible(true)}
            text={t('student.student.button4')}
            writeAuthYn={writeAuthYn}
          />
        </div>
      </div>
      <Table
        className='comm-table__area'
        columns={columns}
        dataSource={studentCoupons || undefined}
        pagination={{
          current: page,
          pageSize: config.pageSize
        }}
      />
      <div className="comm-bx__between">
        <div className="common-paging center">
          <Pagination
            count={studentCoupons?.length || 0}
            page={page}
            setPage={setPage}
          />
        </div>
      </div>

      <IssueCouponModal
        visible={issueCouponVisible}
        setVisible={setIssueCouponVisible}
        student={student}
        getStudentCoupons={getStudentCoupons}
      />
      {/* 쿠폰 삭제 모달 */}
      <Modal
        title={t('student.student.title17')}
        visible={deleteCouponVisible}
        onCancel={() => {
          setCouponCode(null);
          setDeleteCouponVisible(false);
        }}
        width={400}
        footer={[
          <div className='comm-btn__area justify-content-center'>
            <AntButton
              key="submit"
              className='btn-basic pink'
              onClick={onDeleteCoupon}
            >
              {t('common.button.delete')}
            </AntButton>
            <AntButton
              key="cancel"
              className='btn-basic gray'
              onClick={() => {
                setCouponCode(null);
                setDeleteCouponVisible(false);
              }}
            >
              {t('common.button.cancel')}
            </AntButton>
          </div>
        ]}
      >
        {t('system.menu.text3')}
      </Modal>
    </>
  );
}

export default StudentCoupon;
